import {type DidString} from '@atproto/syntax'

import packageJson from '#/../package.json'

/**
 * The semver version of the app, as defined in `package.json.`
 *
 * N.B. The fallback is needed for Render.com deployments
 */
export const RELEASE_VERSION: string =
  process.env.EXPO_PUBLIC_RELEASE_VERSION || packageJson.version

/**
 * The env the app is running in e.g. development, testflight, production, e2e
 */
export const ENV: string = process.env.EXPO_PUBLIC_ENV as
  'production' | 'testflight' | 'development' | 'e2e' | (string & {})

/**
 * Indicates whether the app is running in TestFlight
 */
export const IS_TESTFLIGHT = ENV === 'testflight'

/**
 * Indicates whether the app is `__DEV__`
 */
export const IS_DEV = __DEV__

/**
 * Indicates whether the app is running in a test environment
 */
export const IS_E2E = ENV === 'e2e'

/**
 * Indicates whether the app is `__DEV__` or TestFlight
 */
export const IS_INTERNAL = IS_DEV || IS_TESTFLIGHT

/**
 * Spaces is an alpha protocol and remains opt-in until the mobile crypto and
 * multi-PDS sync paths have passed focused native validation.
 */
export const SPACES_ALPHA_ENABLED =
  process.env.EXPO_PUBLIC_SPACES_ALPHA_ENABLED === '1'

/**
 * Explicit operator acknowledgement for a production web bundle that needs
 * the alpha-backed community board. This does not make the alpha protocol a
 * production-ready standard; it prevents an accidental production opt-in by
 * requiring a second, separately named build setting.
 */
export const SPACES_ALPHA_PRODUCTION_ENABLED =
  process.env.EXPO_PUBLIC_SPACES_ALPHA_PRODUCTION_ENABLED === '1'

const SPACES_ALPHA_ALLOWED_ENVIRONMENTS = new Set([
  'development',
  'test',
  'e2e',
  'testflight',
])

export function isSpacesAlphaDeploymentSafe(
  environment: string | undefined,
  enabled: boolean,
  productionEnabled = false,
): boolean {
  return (
    !enabled ||
    SPACES_ALPHA_ALLOWED_ENVIRONMENTS.has(environment ?? '') ||
    (environment === 'production' && productionEnabled)
  )
}

/**
 * Spaces alpha is permitted only in explicitly named non-production builds,
 * or in a production build with the separate explicit operator opt-in. An
 * unset or unknown environment fails closed instead of being treated as a
 * development bundle.
 */
export function assertSpacesAlphaDeploymentSafe(): void {
  if (
    !isSpacesAlphaDeploymentSafe(
      ENV,
      SPACES_ALPHA_ENABLED,
      SPACES_ALPHA_PRODUCTION_ENABLED,
    )
  ) {
    throw new Error(
      `Spaces alpha is test-only and cannot be enabled in environment: ${ENV || 'unset'}`,
    )
  }
}

export const LEGACY_RADLIB_PRIVATE_ENABLED =
  process.env.EXPO_PUBLIC_LEGACY_RADLIB_PRIVATE_ENABLED === '1'

/**
 * The commit hash that the current bundle was made from. The user can
 * see the commit hash in the app's settings along with the other version info.
 * Useful for debugging/reporting.
 */
export const BUNDLE_IDENTIFIER: string =
  process.env.EXPO_PUBLIC_BUNDLE_IDENTIFIER || 'dev'

/**
 * This will always be in the format of YYMMDDHH, so that it always increases
 * for each build. This should only be used for analytics reporting and shouldn't
 * be used to identify a specific bundle.
 */
export const BUNDLE_DATE: number =
  process.env.EXPO_PUBLIC_BUNDLE_DATE === undefined
    ? 0
    : Number(process.env.EXPO_PUBLIC_BUNDLE_DATE)

/**
 * The log level for the app.
 */
export const LOG_LEVEL = (process.env.EXPO_PUBLIC_LOG_LEVEL || 'info') as
  'debug' | 'info' | 'warn' | 'error'

/**
 * Enable debug logs for specific logger instances
 */
export const LOG_DEBUG: string = process.env.EXPO_PUBLIC_LOG_DEBUG || ''

/**
 * The DID of this deployment's AppView read service.
 *
 * Public reads must have an explicit, deployment-owned authority.  There is
 * deliberately no Bluesky fallback here: an unset production configuration
 * must fail as an unavailable service rather than silently switching the
 * product's read authority.
 */
export const APPVIEW_PROXY_DID: DidString = (process.env
  .EXPO_PUBLIC_APPVIEW_SERVICE_DID ||
  (IS_DEV
    ? 'did:plc:dw4kbjf5mn7nhenabiqpkyh3'
    : 'did:example:unconfigured-appview')) as DidString

export const APPVIEW_PROXY_FRAGMENT =
  process.env.EXPO_PUBLIC_APPVIEW_SERVICE_FRAGMENT || 'bsky_appview'

export const APPVIEW_PROXY_SERVICE =
  `${APPVIEW_PROXY_DID}#${APPVIEW_PROXY_FRAGMENT}` as `${DidString}#${string}`

export const PUBLIC_APPVIEW_URL =
  process.env.EXPO_PUBLIC_PUBLIC_APPVIEW_URL ||
  process.env.EXPO_PUBLIC_APPVIEW_ENDPOINT ||
  'https://appview.invalid'

/** Default labelers are an operator/user configuration, never a hidden app authority. */
const DEFAULT_LABELER_DIDS_ENV = process.env
  .EXPO_PUBLIC_DEFAULT_LABELER_DIDS as string | undefined

export const DEFAULT_LABELER_DIDS: DidString[] = (
  DEFAULT_LABELER_DIDS_ENV || ''
)
  .split(',')
  .map((value: string) => value.trim())
  .filter(Boolean)
  .map((value: string) => value as DidString)

/**
 * The DID of the chat service to proxy to
 */
export const CHAT_PROXY_DID: DidString =
  process.env.EXPO_PUBLIC_CHAT_PROXY_DID || 'did:web:api.bsky.chat'

/**
 * Metrics API host
 */
export const METRICS_API_HOST: string =
  process.env.EXPO_PUBLIC_METRICS_API_HOST || 'https://events.bsky.app'

/**
 * Growthbook API host
 */
export const GROWTHBOOK_API_HOST: string =
  process.env.EXPO_PUBLIC_GROWTHBOOK_API_HOST || `${METRICS_API_HOST}/gb`

/**
 * Growthbook client key
 */
export const GROWTHBOOK_CLIENT_KEY: string =
  process.env.EXPO_PUBLIC_GROWTHBOOK_CLIENT_KEY || 'sdk-7gkUkGy9wguUjyFe'

/**
 * Sentry DSN for telemetry
 */
export const SENTRY_DSN: string | undefined = process.env.EXPO_PUBLIC_SENTRY_DSN

/**
 * Bitdrift API key. If undefined, Bitdrift should be disabled.
 */
export const BITDRIFT_API_KEY: string | undefined =
  process.env.EXPO_PUBLIC_BITDRIFT_API_KEY

/**
 * GCP project ID which is required for native device attestation. On web, this
 * should be unset and evaluate to 0.
 */
export const GCP_PROJECT_ID: number =
  process.env.EXPO_PUBLIC_GCP_PROJECT_ID === undefined
    ? 0
    : Number(process.env.EXPO_PUBLIC_GCP_PROJECT_ID)

/**
 * URLs for the app config web worker. Can be a
 * locally running server, see `env.example` for more.
 */
export const GEOLOCATION_DEV_URL = process.env.GEOLOCATION_DEV_URL
export const GEOLOCATION_PROD_URL = `https://ip.bsky.app`
export const GEOLOCATION_URL = IS_DEV
  ? (GEOLOCATION_DEV_URL ?? GEOLOCATION_PROD_URL)
  : GEOLOCATION_PROD_URL

/**
 * URLs for the live-event config web worker. Can be a
 * locally running server, see `env.example` for more.
 */
export const LIVE_EVENTS_DEV_URL = process.env.LIVE_EVENTS_DEV_URL
export const LIVE_EVENTS_PROD_URL = `https://live-events.workers.bsky.app`
export const LIVE_EVENTS_URL = IS_DEV
  ? (LIVE_EVENTS_DEV_URL ?? LIVE_EVENTS_PROD_URL)
  : LIVE_EVENTS_PROD_URL

/**
 * URLs for the app-config web worker. Can be a
 * locally running server, see `env.example` for more.
 */
export const APP_CONFIG_DEV_URL = process.env.APP_CONFIG_DEV_URL
export const APP_CONFIG_PROD_URL = `https://app-config.workers.bsky.app`
export const APP_CONFIG_URL = IS_DEV
  ? (APP_CONFIG_DEV_URL ?? APP_CONFIG_PROD_URL)
  : APP_CONFIG_PROD_URL
